import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { User, SendIcon, ArrowLeft } from 'lucide-react'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'
import clsx from 'clsx'

type NAV_ITEM = {
  name: string
  description: string
  icon: React.ElementType<{ className?: string }>
  // IMPORTANT: This path needs to be the same as the one in the router otherwise this won't watch changes on the URL correctly
  path: string
  isActive: boolean
}

const LOGO_URL = '/images/intuai-logo.svg'

type OnBoardingItemProps = {
  item: NAV_ITEM
}

const OnboardingNavItem = ({ item }: OnBoardingItemProps) => {
  const isRTL = getCurrentDirection() === 'rtl'
  
  return (
    <li
      className={clsx(
        "flex gap-5 transition-all",
        isRTL && "flex-row-reverse",
        item.isActive ? 'opacity-100' : 'opacity-60'
      )}
    >
      <figure className="w-[48px] h-[48px] flex bg-white dark:bg-dark-card rounded-md border-gray-200 dark:border-dark-border border justify-center items-center">
        <item.icon className="w-[24px] h-[24px] text-gray-600 dark:text-gray-400" />
      </figure>
      <div className={clsx(isRTL && "text-right")}>
        <p className="text-gray-600 dark:text-gray-400 font-semibold">{item.name}</p>
        <p className="text-gray-400 dark:text-gray-500">{item.description}</p>
      </div>
    </li>
  )
}

const OnboardingNav = () => {
  const { t } = useTranslation()
  const { logout } = useAppStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isRTL = getCurrentDirection() === 'rtl'

  const ITEMS: NAV_ITEM[] = [
    {
      name: t('account:onboarding.nav.account_details'),
      description: t('account:onboarding.nav.account_details_description'),
      path: 'onboarding',
      icon: User,
      isActive: true,
    },
  ]

  const [currentStepState, setStepState] = useState(ITEMS)

  const handleBack = () => {
    navigate(-1)
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  useEffect(() => {
    const pathname = location.pathname.split('/')
    const currentUrlPath = pathname[pathname.length - 1]

    // Regenerate ITEMS with current translations
    const currentItems: NAV_ITEM[] = [
      {
        name: t('account:onboarding.nav.account_details'),
        description: t('account:onboarding.nav.account_details_description'),
        path: 'onboarding',
        icon: User,
        isActive: true,
      },
    ]

    // TODO: Test this functionality
    const newItemsList = currentItems.map((item) =>
      item.path === currentUrlPath
        ? { ...item, isActive: true }
        : { ...item, isActive: false }
    )

    setStepState(newItemsList)
  }, [location, t])

  return (
    <nav className={clsx(
      "flex flex-col flex-2 justify-between h-full bg-slate-50 dark:bg-dark-muted min-w-[300px] p-5",
      isRTL ? "border-l border-gray-200 dark:border-dark-border" : "border-r border-gray-200 dark:border-dark-border"
    )}>
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <button
            onClick={handleBack}
            className={clsx(
              "text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1",
              isRTL ? "flex-row-reverse self-end" : "self-start"
            )}
          >
            <ArrowLeft size={16} className={clsx(isRTL && "rotate-180")} />
            {t('account:onboarding.nav.back')}
          </button>
          <figure className={clsx(
            "flex",
            isRTL ? "justify-end" : "justify-start"
          )}>
          <img
            src={LOGO_URL}
            width={140}
            alt="logo"
            draggable={false}
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/chat')}
          />
          </figure>
        </div>
        <ul className="flex flex-col gap-8">
          {currentStepState.map((item) => (
            <OnboardingNavItem item={item} key={item.name} />
          ))}
        </ul>
      </div>
      <footer className={clsx(
        "flex h-auto items-center",
        isRTL ? "flex-row-reverse justify-between" : "justify-between"
      )}>
        <Button variant="outline" onClick={handleLogout}>
          {t('account:onboarding.nav.logout')}
        </Button>
        <span className={clsx(
          "flex gap-3 items-center text-gray-500 dark:text-dark-text-muted",
          isRTL && "flex-row-reverse"
        )}>
          <SendIcon className="w-[16px] h-[16px]" />
          <span>{t('account:onboarding.nav.help_email')}</span>
        </span>
      </footer>
    </nav>
  )
}

export default OnboardingNav
